import SectionHeading from '@/components/feature/SectionHeading';
import { recommendations } from '@/mocks/recommendations';
import { genres } from '@/mocks/genres';

const seasons = ['全部', '本季新番', '往季补番', '深夜档'];
const genreOptions = ['全部', ...genres.map((g) => g.name)];

interface RecommendationGridProps {
  activeSeason: string;
  onSeasonChange: (season: string) => void;
  activeGenre: string;
  onGenreChange: (genre: string) => void;
  watchlist: string[];
  onToggleWatch: (id: string) => void;
}

export default function RecommendationGrid({
  activeSeason,
  onSeasonChange,
  activeGenre,
  onGenreChange,
  watchlist,
  onToggleWatch,
}: RecommendationGridProps) {
  const list = recommendations.filter((item) => {
    const matchSeason = activeSeason === '全部' || item.season === activeSeason;
    const matchGenre = activeGenre === '全部' || item.genres.includes(activeGenre);
    return matchSeason && matchGenre;
  });

  return (
    <section id="recommend" className="w-full bg-background-100 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeading
          eyebrow="新番推荐"
          title="按季度与频道挑，看到想看的再点追番"
          desc="下面每一条都包含看点标签、评分、更新进度与一句剧情简介。用季度标签切换「正在追更 / 已完结补番 / 深夜档」，再用频道标签收窄范围即可。"
        />

        <div className="flex flex-col gap-3 mb-8">
          <div className="inline-flex flex-wrap gap-1 rounded-full bg-background-200/70 p-1 self-start">
            {seasons.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => onSeasonChange(s)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  activeSeason === s
                    ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                    : 'text-foreground-600 hover:text-foreground-950'
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            {genreOptions.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => onGenreChange(g)}
                className={`px-3 py-1.5 rounded-full border text-xs md:text-sm whitespace-nowrap cursor-pointer transition-colors ${
                  activeGenre === g
                    ? 'bg-secondary-500 border-secondary-500 text-background-50 dark:text-foreground-950'
                    : 'bg-background-50 border-background-300 text-foreground-600 hover:border-secondary-400 hover:text-secondary-700'
                }`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        <p className="mb-5 text-sm text-foreground-500">
          当前筛选：{activeSeason} · {activeGenre} — 共 {list.length} 部作品
        </p>

        {list.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            <p className="mt-3 text-sm text-foreground-600">该组合下暂时没有条目，换个频道或季度再看看。</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {list.map((item) => {
              const watched = watchlist.includes(item.id);
              return (
                <article
                  key={item.id}
                  className="group flex flex-col rounded-lg border border-background-200 bg-background-50 overflow-hidden"
                >
                  <div className="relative w-full aspect-[3/4] overflow-hidden bg-background-200">
                    <img
                      src={item.image}
                      alt={`${item.title} 封面`}
                      title={`${item.title} 推荐作品`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-3 left-3 rounded-full bg-accent-500 px-2.5 py-1 text-xs font-bold text-foreground-950">
                      {item.score.toFixed(1)}
                    </span>
                    <span className="absolute top-3 right-3 rounded-full bg-foreground-950/70 px-2.5 py-1 text-xs font-medium text-background-50 backdrop-blur">
                      {item.season}
                    </span>
                  </div>

                  <div className="flex flex-col flex-1 p-4 md:p-5">
                    <h3 className="font-heading text-lg md:text-xl text-foreground-950">{item.title}</h3>
                    <p className="mt-1 text-xs md:text-sm text-secondary-700">{item.tagline}</p>

                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {item.tags.map((tag) => (
                        <span key={tag} className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900">
                          {tag}
                        </span>
                      ))}
                    </div>

                    <p className="mt-3 text-sm text-foreground-600 leading-relaxed flex-1">{item.desc}</p>

                    <div className="mt-4 flex items-center justify-between gap-3 text-xs text-foreground-500">
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {item.episodes}
                      </span>
                      <span className="inline-flex items-center gap-1 truncate">
                        <i className="ri-calendar-line"></i>
                        {item.air}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => onToggleWatch(item.id)}
                      aria-pressed={watched}
                      className={`mt-4 inline-flex items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                        watched
                          ? 'bg-secondary-100 text-secondary-900 hover:bg-secondary-200'
                          : 'bg-primary-500 text-background-50 hover:bg-primary-600 dark:text-foreground-950'
                      }`}
                    >
                      <i className={watched ? 'ri-bookmark-fill' : 'ri-bookmark-line'}></i>
                      {watched ? '已在追番清单' : '加入追番'}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}